import {API, getApiPath} from '@/lib/utils/api.utils';
import {isError} from '@epic-mw/error-types';
import useSWRImmutable from 'swr/immutable';
import {useLocale} from '@epic-mw/i18n';
import {useAccountId} from '../account';
import {useCountry} from '../geo';
import {AccountTotalBalance} from '@/types';
import {useCurrencyDetail} from '../catalog';
import {useMemo} from 'react';
import {formatCurrency} from '@/lib/utils/giftCard.utils';
import {mutate} from 'swr';

export const getAccountTotalBalance = async (
	params: Record<string, string>
): Promise<AccountTotalBalance> => {
	try {
		const query = new URLSearchParams(params);
		const response = await API<AccountTotalBalance>(
			`${getApiPath()}/account/total-balance?${query}`,
			{
				method: 'GET',
				withCredentials: true,
				headers: {
					'Cache-Control': 'no-store, no-cache',
					Expires: '0'
				}
			}
		);
		return response?.data;
	} catch (err) {
		console.error('auth() failed to load', isError(err) && err.message);
		throw err;
	}
};

const ACCOUNT_TOTAL_BALANCE_KEY = '/api/account/total-balance';

export const refreshAccountTotalBalance = () => {
	return mutate(ACCOUNT_TOTAL_BALANCE_KEY).catch((err) => {
		console.error(err);
	});
};

export const useAccountTotalBalance = () => {
	const locale = useLocale();
	const country = useCountry();
	const accountId = useAccountId();
	const shouldFetch = !!country && !!accountId && !!locale;

	const {data: accountBalanceData, error} = useSWRImmutable(
		shouldFetch ? ACCOUNT_TOTAL_BALANCE_KEY : null,
		() => getAccountTotalBalance({locale, country}),
		{
			errorRetryCount: 1
		}
	);

	const currencyCode = accountBalanceData?.currency;

	const details = useCurrencyDetail(currencyCode!);

	const data = useMemo(() => {
		if (!details?.currency) {
			return undefined;
		}

		return {
			...accountBalanceData,
			formattedBalance: formatCurrency(
				accountBalanceData?.totalBalance || 0,
				String(details.currency?.code),
				locale,
				Number(details.currency?.decimals)
			)
		};
	}, [details?.currency, accountBalanceData?.totalBalance]);

	return {
		data,
		isLoading: details.isLoading || (!accountBalanceData && !error),
		error: error || details.error
	};
};
